import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Divider, Stack, Tooltip,
  FormControlLabel, Switch,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../../services/api";
import LoadingSkeleton from "../../../components/common/LoadingSkeleton";
import EmptyState from "../../../components/admin/EmptyState";
import useToastStore from "../../../store/toastStore";
import { getStatusChip, formatItemsText, calcTotalPrice, formatPrice } from "./marketUtils";

const EXPORT_CODES = [
  { code: "KITCHENWARE", name: "식기/주방용품" },
  { code: "TOY", name: "인형/장난감" },
  { code: "ELECTRONICS", name: "가전/전자제품" },
  { code: "FURNITURE", name: "가구/침구류" },
  { code: "BABY_ITEM", name: "유모차/아이용 전동자동차" },
  { code: "CLOSING_BUSINESS", name: "폐업 물품" },
  { code: "RELIC_OR_MOVE_CLEANUP", name: "유품/이사정리" },
];

/**
 * 승인 대기 탭 — AWAITING_APPROVAL 상태의 수거 요청을 관리자가 검토하고
 * 사용자/작업자 사진을 비교한 뒤 승인 또는 거절(사유 필수) 한다.
 *
 * 승인 시 백엔드 PhotoCleanupService 가 비동기로 S3+DB 사진을 모두 삭제(휘발성).
 * 거절 시 작업자 사진은 동기 삭제, 위치 status REJECTED, 작업자/사용자 양쪽 알림.
 */
export default function MarketApprovalTab({ readOnly = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedLoc, setSelectedLoc] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [exportItems, setExportItems] = useState([]);

  const [lightboxUrl, setLightboxUrl] = useState(null);

  const showToast = useToastStore((s) => s.showToast);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/market-locations/awaiting-approval");
      setList(res.data || []);
    } catch (err) {
      console.error("승인 대기 목록 조회 실패:", err);
      showToast("승인 대기 목록을 불러오지 못했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = async (loc) => {
    setSelectedLoc(loc);
    setExportItems(defaultExportItems(loc));
    setPhotos([]);
    setPhotosLoading(true);
    try {
      const res = await api.get(`/api/market-photos/location/${loc.marketLocationId}`);
      setPhotos(res.data || []);
    } catch (err) {
      console.error("사진 조회 실패:", err);
      showToast("사진을 불러오지 못했습니다.", "error");
    } finally {
      setPhotosLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedLoc(null);
    setPhotos([]);
    setRejectOpen(false);
    setRejectReason("");
    setExportItems([]);
  };

  const handleApprove = async () => {
    if (!selectedLoc) return;
    setActionLoading(true);
    try {
      await api.post(`/api/market-locations/${selectedLoc.marketLocationId}/approve`, {
        exportItems: exportItems.filter((item) => item.itemCode && item.itemName && Number(item.quantity) > 0),
      });
      showToast("승인 완료. 사진은 곧 자동 삭제됩니다.", "success");
      closeDetail();
      fetchList();
    } catch (err) {
      console.error("승인 실패:", err);
      const msg = err.response?.data?.message || "승인 처리 중 오류가 발생했습니다.";
      showToast(msg, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedLoc) return;
    const reason = rejectReason.trim();
    if (!reason) {
      showToast("거절 사유를 입력해주세요.", "warning");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/api/market-locations/${selectedLoc.marketLocationId}/reject`, { reason });
      showToast("거절 완료. 작업자/사용자에게 알림이 전송되었습니다.", "success");
      closeDetail();
      fetchList();
    } catch (err) {
      console.error("거절 실패:", err);
      const msg = err.response?.data?.message || "거절 처리 중 오류가 발생했습니다.";
      showToast(msg, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const userPhotos = photos.filter((p) => p.uploaderRole === "USER");
  const collectorPhotos = photos.filter((p) => p.uploaderRole === "COLLECTOR");
  const canApprove = exportItems.length > 0 && exportItems.every((item) => item.itemCode && item.itemName && Number(item.quantity) > 0);

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              승인 대기 목록
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {readOnly
                ? "작업자가 수거 완료 신청한 건과 사진, 수출 품목 입력 항목을 조회합니다."
                : "작업자가 수거 완료 신청한 건을 검토하고 승인 또는 거절합니다. 승인 시 사진은 자동으로 삭제됩니다."}
            </Typography>
          </Box>
          <Button
            variant="outlined" startIcon={<RefreshIcon />} onClick={fetchList} disabled={loading}
          >
            새로고침
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingSkeleton rows={5} />
      ) : list.length === 0 ? (
        <EmptyState message="승인 대기 중인 수거가 없습니다." />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>주소</TableCell>
                <TableCell>희망 날짜</TableCell>
                <TableCell>이용자</TableCell>
                <TableCell>작업자</TableCell>
                <TableCell>품목</TableCell>
                <TableCell align="right">예상 금액</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="center">검토</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((loc) => (
                <TableRow hover key={loc.marketLocationId}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {loc.address || "-"}
                    </Typography>
                    {loc.detailAddress && (
                      <Typography variant="caption" color="text.secondary">
                        {loc.detailAddress}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{loc.desiredDate || "-"}</TableCell>
                  <TableCell>{loc.userName || "-"}</TableCell>
                  <TableCell>{loc.collectorName || "-"}</TableCell>
                  <TableCell>
                    <Tooltip title={formatItemsText(loc.items)}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {formatItemsText(loc.items)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{formatPrice(calcTotalPrice(loc))}</TableCell>
                  <TableCell>{getStatusChip(loc.status)}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small" variant="contained" startIcon={<VisibilityIcon />}
                      onClick={() => openDetail(loc)}
                    >
                      {readOnly ? "보기" : "검토"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 검토 다이얼로그 — 사진 비교 + 승인/거절 */}
      <Dialog
        open={!!selectedLoc} onClose={closeDetail}
        maxWidth="md" fullWidth
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #2a9ac6 0%, #27ae60 100%)",
            color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center",
          }}
        >
          {readOnly ? "수거 완료 상세" : "수거 완료 검토"}
          <IconButton onClick={closeDetail} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedLoc && (
            <>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">주소</Typography>
                  <Typography variant="body1">
                    {selectedLoc.address}
                    {selectedLoc.detailAddress ? ` (${selectedLoc.detailAddress})` : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 4 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">이용자</Typography>
                    <Typography variant="body2">{selectedLoc.userName || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">작업자</Typography>
                    <Typography variant="body2">{selectedLoc.collectorName || "-"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">희망 날짜</Typography>
                    <Typography variant="body2">{selectedLoc.desiredDate || "-"}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">품목</Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                    {(selectedLoc.items || []).map((it, idx) => (
                      <Chip key={idx} label={`${it.name} x${it.quantity}`} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {photosLoading ? (
                <LoadingSkeleton rows={2} />
              ) : (
                <Box sx={{ display: "flex", gap: 3, flexWrap: { xs: "wrap", md: "nowrap" } }}>
                  <PhotoColumn
                    title="이용자 등록 사진" subtitle="요청 시 등록"
                    photos={userPhotos} onClickThumb={setLightboxUrl} accent="#2a9ac6"
                  />
                  <PhotoColumn
                    title="작업자 수거 사진" subtitle="완료 신청 시 등록"
                    photos={collectorPhotos} onClickThumb={setLightboxUrl} accent="#27ae60"
                  />
                </Box>
              )}

              {!rejectOpen && !readOnly ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>수출 품목 코드</Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {exportItems.map((item, index) => (
                      <Box key={index} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 88px 120px 120px 40px" }, gap: 1, alignItems: "center" }}>
                        <TextField
                          select size="small" label="품목 코드" value={item.itemCode}
                          SelectProps={{ native: true }}
                          onChange={(e) => {
                            const selected = EXPORT_CODES.find((code) => code.code === e.target.value);
                            setExportItems((prev) => prev.map((row, i) => i === index
                              ? { ...row, itemCode: e.target.value, itemName: selected?.name || row.itemName }
                              : row));
                          }}
                        >
                          {EXPORT_CODES.map((code) => (
                            <option key={code.code} value={code.code}>{code.code}</option>
                          ))}
                        </TextField>
                        <TextField
                          size="small" label="품목명" value={item.itemName}
                          onChange={(e) => setExportItems((prev) => prev.map((row, i) => i === index ? { ...row, itemName: e.target.value } : row))}
                        />
                        <TextField
                          size="small" label="수량" type="number" value={item.quantity}
                          onChange={(e) => setExportItems((prev) => prev.map((row, i) => i === index ? { ...row, quantity: Number(e.target.value) } : row))}
                          inputProps={{ min: 1 }}
                        />
                        <FormControlLabel
                          control={<Switch size="small" checked={item.exportAvailable !== false}
                            onChange={(e) => setExportItems((prev) => prev.map((row, i) => i === index ? { ...row, exportAvailable: e.target.checked } : row))} />}
                          label="수출 가능"
                        />
                        <FormControlLabel
                          control={<Switch size="small" checked={Boolean(item.exceptionItem)}
                            onChange={(e) => setExportItems((prev) => prev.map((row, i) => i === index ? { ...row, exceptionItem: e.target.checked } : row))} />}
                          label="예외 품목"
                        />
                        <IconButton
                          aria-label="품목 삭제"
                          onClick={() => setExportItems((prev) => prev.filter((_, i) => i !== index))}
                          disabled={exportItems.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <TextField
                      size="small"
                      label="관리자 검수 메모"
                      multiline
                      minRows={2}
                      value={exportItems[0]?.inspectionMemo || ""}
                      onChange={(e) => setExportItems((prev) => prev.map((row) => ({ ...row, inspectionMemo: e.target.value })))}
                      inputProps={{ maxLength: 500 }}
                    />
                    <Button
                      size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => setExportItems((prev) => [...prev, { itemCode: "KITCHENWARE", itemName: "식기/주방용품", quantity: 1, exportAvailable: true, exceptionItem: false, inspectionMemo: prev[0]?.inspectionMemo || "" }])}
                    >
                      품목 추가
                    </Button>
                  </Stack>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    승인 시 사진은 자동으로 삭제됩니다(되돌릴 수 없음).
                    문제가 있다면 거절 → 사유와 함께 작업자/사용자에게 알림이 전송됩니다.
                  </Alert>
                </>
              ) : rejectOpen ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="error" sx={{ mb: 1 }}>
                    거절 사유는 작업자에게 알림으로 전달됩니다. 명확하게 작성해주세요.
                  </Alert>
                  <TextField
                    fullWidth multiline minRows={3} maxRows={6}
                    label="거절 사유 (필수, 500자 이내)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    placeholder="예: 침대 프레임 1개가 빠진 것으로 보입니다. 추가 수거 부탁드립니다."
                  />
                </>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {readOnly ? (
            <Button onClick={closeDetail}>닫기</Button>
          ) : !rejectOpen ? (
            <>
              <Button onClick={closeDetail} disabled={actionLoading}>닫기</Button>
              <Button
                color="error" variant="outlined" startIcon={<CancelIcon />}
                onClick={() => setRejectOpen(true)} disabled={actionLoading}
              >
                거절
              </Button>
              <Button
                color="success" variant="contained" startIcon={<CheckCircleIcon />}
                onClick={handleApprove} disabled={actionLoading || !canApprove}
              >
                {actionLoading ? "처리 중..." : "승인"}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setRejectOpen(false); setRejectReason(""); }} disabled={actionLoading}>
                되돌리기
              </Button>
              <Button
                color="error" variant="contained" startIcon={<CancelIcon />}
                onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}
              >
                {actionLoading ? "처리 중..." : "거절 확정"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Lightbox — 사진 클릭 시 확대 */}
      <Dialog open={!!lightboxUrl} onClose={() => setLightboxUrl(null)} maxWidth="lg">
        <Box sx={{ position: "relative", bgcolor: "#000" }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{ position: "absolute", top: 8, right: 8, color: "#fff", bgcolor: "rgba(0,0,0,0.4)", "&:hover": { bgcolor: "rgba(0,0,0,0.6)" } }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxUrl && (
            <img
              src={lightboxUrl} alt="확대 보기"
              style={{ display: "block", maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
}

function defaultExportItems(loc) {
  const items = loc.items || [];
  if (items.length === 0) {
    return [{ itemCode: "KITCHENWARE", itemName: "식기/주방용품", quantity: 1, exportAvailable: true, exceptionItem: false, inspectionMemo: "" }];
  }
  return items.map((item) => {
    const matched = EXPORT_CODES.find((code) => item.name?.includes(code.name.split("/")[0]));
    return {
      itemCode: matched?.code || "KITCHENWARE",
      itemName: matched?.name || item.name || "식기/주방용품",
      quantity: item.quantity || 1,
      exportAvailable: true,
      exceptionItem: false,
      inspectionMemo: "",
    };
  });
}

function PhotoColumn({ title, subtitle, photos, onClickThumb, accent }) {
  return (
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ width: 8, height: 24, bgcolor: accent, borderRadius: 1 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle} · {photos.length}장</Typography>
        </Box>
      </Box>
      {photos.length === 0 ? (
        <Box sx={{ p: 2, border: "1px dashed", borderColor: "divider", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">등록된 사진이 없습니다.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 1 }}>
          {photos.map((p) => (
            <Box
              key={p.photoId}
              onClick={() => onClickThumb(p.imageUrl)}
              sx={{
                position: "relative", paddingTop: "100%", borderRadius: 1, overflow: "hidden",
                cursor: "zoom-in", border: "1px solid", borderColor: "divider",
                "&:hover": { borderColor: accent, transform: "scale(1.02)" },
                transition: "transform 0.15s ease, border-color 0.15s ease",
              }}
            >
              <img
                src={p.imageUrl} alt="수거 사진"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                loading="lazy"
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
